import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Loader2, Smartphone, User, Car, Store } from "lucide-react";
import { toast } from "sonner";
import AuthLayout from "../../components/auth/AuthLayout";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { useAuth, homeForRole } from "../../context/AuthContext";
import { errorMessage } from "../../lib/api";
import { GoogleButton } from "../../components/auth/GoogleAuth";

const SOCIAL_ROLES = [
  { value: "rider", label: "Customer", icon: User },
  { value: "driver", label: "Driver", icon: Car },
  { value: "restaurant", label: "Restaurant", icon: Store },
];

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [socialRole, setSocialRole] = useState("rider");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const user = await login(email.trim(), password);
      toast.success(`Welcome back, ${user.name}`);
      navigate(location.state?.from || homeForRole(user.role), { replace: true });
    } catch (err) {
      const d = err?.response?.data?.detail;
      if (err?.response?.status === 403 && d && typeof d === "object" && d.code === "password_expired") {
        toast.error("Your password has expired. Please reset it to continue.");
        navigate("/auth/forgot-password", { state: { email: d.email || email.trim(), expired: true } });
        return;
      }
      toast.error(errorMessage(err, "Login failed"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthLayout
      title="Welcome back"
      subtitle="Log in with your email or mobile number and password."
      footer={<>New here? <Link to="/auth/signup" className="font-semibold text-brand hover:underline" data-testid="link-signup">Create an account</Link></>}
    >
      <form onSubmit={submit} className="space-y-5" data-testid="login-form">
        <div className="space-y-2">
          <p className="text-xs font-medium text-ink-muted">Choose your role, then continue with Google</p>
          <div className="grid grid-cols-3 gap-2" data-testid="social-role">
            {SOCIAL_ROLES.map((r) => (
              <button key={r.value} type="button" onClick={() => setSocialRole(r.value)}
                className={`flex flex-col items-center gap-1 rounded-xl border px-2 py-2 text-xs font-semibold transition-colors ${socialRole === r.value ? "border-brand bg-brand-50 text-brand" : "border-ink/15 text-ink-soft hover:bg-surface-100"}`}
                data-testid={`social-role-${r.value}`}>
                <r.icon className="h-4 w-4" /> {r.label}
              </button>
            ))}
          </div>
        </div>
        <GoogleButton role={socialRole} label={`Continue with Google as ${SOCIAL_ROLES.find((r) => r.value === socialRole)?.label}`} />
        <div className="relative py-2 text-center text-xs uppercase tracking-wider text-ink-muted">
          <span className="relative z-10 bg-surface px-3">or log in with password</span>
          <span className="absolute inset-x-0 top-1/2 -z-0 h-px bg-surface-300" />
        </div>
        <div className="space-y-2">
          <Label htmlFor="email">Email or mobile number</Label>
          <Input id="email" type="text" autoComplete="username" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com or +91 98765 43210" className="h-11" data-testid="login-email" />
        </div>
        <div className="space-y-2">
          <div className="flex items-center justify-between"><Label htmlFor="password">Password</Label><Link to="/auth/forgot-password" className="text-xs font-semibold text-brand hover:underline" data-testid="login-forgot-link">Forgot password?</Link></div>
          <Input id="password" type="password" autoComplete="current-password" required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Your password" className="h-11" data-testid="login-password" />
        </div>
        <Button type="submit" disabled={busy} className="h-11 w-full rounded-full bg-brand text-white hover:bg-brand-600" data-testid="login-submit">
          {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Log in
        </Button>
        <Button asChild type="button" variant="outline" className="h-11 w-full rounded-full border-ink/15 text-ink hover:bg-surface-200" data-testid="login-phone-link">
          <Link to="/auth/phone" state={{ mode: "login" }}><Smartphone className="mr-2 h-4 w-4" /> Login with phone or email OTP</Link>
        </Button>
      </form>
    </AuthLayout>
  );
}
